import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { brand, gray } from "../../../../theme/themePrimitives";
import { FormValues } from "../AssignmentUploads";

interface ReadingExperienceSectionProps {
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  isPdfUpload: boolean;
  isVideoUpload: boolean;
}

export const ReadingExperienceSection: React.FC<
  ReadingExperienceSectionProps
> = ({ formValues, setFormValues, isPdfUpload, isVideoUpload }) => {
  return (
    <Box
      sx={{
        mt: 3,
        mb: 3,
        backgroundColor: brand[50],
        border: `1px solid ${brand[200]}`,
        borderRadius: "12px",
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1.5 }}>
        <Typography fontSize={16} fontWeight={600}>
          {isVideoUpload ? "Watching experience" : "Reading experience"}
        </Typography>
        <Chip
          label={isPdfUpload ? "PDF" : "Video"}
          size="small"
          variant="outlined"
          sx={{
            height: 22,
            fontSize: "12px",
            borderColor: brand[400],
            backgroundColor: brand[50],
            "& .MuiChip-label": { color: brand[700], fontWeight: 400 },
          }}
        />
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <FormControlLabel
            control={
              <Switch
                color="success"
                checked={formValues.acknowledgement}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    acknowledgement: e.target.checked,
                  }))
                }
              />
            }
            label={
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="body2" fontWeight={600}>
                  Require acknowledgement
                </Typography>
                <Tooltip
                  title="Shows an acknowledgement panel every learner must confirm."
                  arrow
                  placement="top"
                  slotProps={{
                    tooltip: { sx: { bgcolor: "#1F2937", color: "#fff" } },
                    arrow: { sx: { color: "#1F2937" } },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 20, color: gray[500] }} />
                </Tooltip>
              </Stack>
            }
            sx={{ marginLeft: 0, gap: 1 }}
          />
        </Box>
        {isPdfUpload && (
          <Box sx={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  color="success"
                  checked={formValues.pageByPage}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      pageByPage: e.target.checked,
                    }))
                  }
                />
              }
              label={
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Page-by-page navigation
                  </Typography>
                  <Tooltip
                    title="Off, the document scrolls freely. On, pages advance in order and the place is bookmarked."
                    arrow
                    placement="top"
                    slotProps={{
                      tooltip: { sx: { bgcolor: "#1F2937", color: "#fff" } },
                      arrow: { sx: { color: "#1F2937" } },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 20, color: gray[500] }} />
                  </Tooltip>
                </Stack>
              }
              sx={{ marginLeft: 0, gap: 1 }}
            />
          </Box>
        )}
        {isVideoUpload && (
          <Box sx={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  color="success"
                  checked={formValues.mandatory}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      mandatory: e.target.checked,
                    }))
                  }
                />
              }
              label={
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Mandatory
                  </Typography>
                  <Tooltip
                    title="On, videos prevent learners from skipping ahead."
                    arrow
                    placement="top"
                    slotProps={{
                      tooltip: { sx: { bgcolor: "#1F2937", color: "#fff" } },
                      arrow: { sx: { color: "#1F2937" } },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 20, color: gray[500] }} />
                  </Tooltip>
                </Stack>
              }
              sx={{ marginLeft: 0, gap: 1 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
